import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import LandAdvisorLeftPane from '../components/land-advisor/LandAdvisorLeftPane';
import LandAdvisorRightPane from '../components/land-advisor/LandAdvisorRightPane';
import { useHideIntercom } from '../utils/hooks';
import { LandAdvisorContextProvider } from '../contexts/LandAdvisorContext';

const MIN_PERCENT = 25;
const MAX_PERCENT = 75;
const DARK_GRAY = '#23272f';

const Container = styled.div<{ dragging: boolean }>`
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  user-select: ${({ dragging }) => (dragging ? 'none' : 'auto')};
`;

// The attrs approach is to avoid frequent style changes when resizing
const LeftPane = styled.div.attrs<{ leftPercent: number }>((props) => ({
  style: {
    width: `${props.leftPercent}%`,
  },
}))<{ leftPercent: number }>`
  min-width: ${MIN_PERCENT}%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: ${DARK_GRAY};
`;

const RightPane = styled.div`
  flex: 1;
  overflow: auto;
  background: #fff;
`;

const DividerButton = styled.button`
  width: 16px;
  background: ${DARK_GRAY};
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding: 0;
  outline: none;
  height: 100%;
  cursor: col-resize;
  &:hover,
  &:active {
    cursor: col-resize;
    background: ${DARK_GRAY};
  }
`;

const DividerHandle = styled.div`
  width: 8px;
  height: 30%;
  background: rgba(68, 72, 81, 0.85);
  border-radius: 6px;
  margin: 0 auto;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12);
`;

const LandAdvisorView = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [leftPercent, setLeftPercent] = useState<number>(30);
  const [dragging, setDragging] = useState<boolean>(false);
  // Hide intercom from this page
  useHideIntercom();

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newPercent = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftPercent(Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, newPercent)));
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!dragging || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      if (!touch) return;
      const newPercent = ((touch.clientX - rect.left) / rect.width) * 100;
      setLeftPercent(Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, newPercent)));
    };

    const stopDrag = () => setDragging(false);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('mouseup', stopDrag);
    window.addEventListener('touchend', stopDrag);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchend', stopDrag);
    };
  }, [dragging]);

  const startDrag = (e?: React.MouseEvent | React.TouchEvent) => {
    e?.preventDefault();
    setDragging(true);
  };

  const onDividerKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 5 : 1;
    if (e.key === 'ArrowLeft') {
      setLeftPercent((p) => Math.max(MIN_PERCENT, p - step));
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      setLeftPercent((p) => Math.min(MAX_PERCENT, p + step));
      e.preventDefault();
    } else if (e.key === 'Home') {
      setLeftPercent(MIN_PERCENT);
      e.preventDefault();
    } else if (e.key === 'End') {
      setLeftPercent(MAX_PERCENT);
      e.preventDefault();
    }
  };

  return (
    <LandAdvisorContextProvider>
      <Container ref={containerRef} dragging={dragging}>
        <LeftPane leftPercent={leftPercent}>
          <LandAdvisorLeftPane />
        </LeftPane>

        <DividerButton
          type="button"
          aria-label="Resize panes"
          onMouseDown={startDrag}
          onTouchStart={startDrag}
          onKeyDown={onDividerKeyDown}
        >
          <DividerHandle />
        </DividerButton>

        <RightPane>
          <LandAdvisorRightPane leftPercent={leftPercent} />
        </RightPane>
      </Container>
    </LandAdvisorContextProvider>
  );
};

export default LandAdvisorView;
